import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Send, Copy, ChevronRight, ChevronDown, ChevronLeft, Sparkles, FileText, CheckCircle, History, Pencil, Check, X, ArrowLeftRight, Save, Paperclip, MessageSquare, ShieldCheck, User as UserIcon, Mail, Download, Building2, Briefcase, FolderKanban, XCircle, CheckCircle2, PanelRightClose, PanelRightOpen } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import DocDiffDialog from "@/components/DocDiffDialog";
import SearchableSelect from "@/components/ui/SearchableSelect";
import InlineNewItemPopover from "@/components/estimates/InlineNewItemPopover";
import EffortBuilder from "@/components/estimates/EffortBuilder";
import DesignationsDialog from "@/components/estimates/DesignationsDialog";
import { apiErrorMessage } from "@/lib/apiError";

const RATE_SOURCE_LABEL = {
  client_price_list: "Client price",
  brand_rate_card: "Brand rate",
  catalog: "Catalog std",
  designation_rate: "Designation",
  custom: "Custom",
};
const RATE_SOURCE_STYLE = {
  client_price_list: "bg-emerald-50 text-emerald-700 border-emerald-300",
  brand_rate_card: "bg-sky-50 text-sky-700 border-sky-300",
  catalog: "bg-slate-100 text-slate-700 border-slate-300",
  designation_rate: "bg-amber-50 text-amber-700 border-amber-300",
  custom: "bg-purple-50 text-purple-700 border-purple-300",
};

function fmtMoney(v, currency = "INR") {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `${currency === "INR" ? "₹" : currency + " "}${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

// Iter 242 · Margin chip — green ≥40 / amber 20-40 / red <20
function MarginChip({ pct, testid }) {
  if (pct === null || pct === undefined) return null;
  const val = Number(pct);
  if (!Number.isFinite(val)) return null;
  const tone = val >= 40 ? "bg-emerald-50 text-emerald-700 border-emerald-300"
              : val >= 20 ? "bg-amber-50 text-amber-700 border-amber-300"
              : "bg-rose-50 text-rose-700 border-rose-300";
  return (
    <div className={`inline-block mt-0.5 px-1.5 py-0.5 text-[9px] border uppercase tracking-wide font-semibold ${tone}`}
          title="Margin % = (billable − cost) / billable"
          data-testid={testid}>
      {val.toFixed(1)}% margin
    </div>
  );
}

export default function EstimateDetail() {
  const { eid } = useParams();
  const nav = useNavigate();
  const { activeWorkspaceId } = useAuth();
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratesHidden, setRatesHidden] = useState(false);
  const [expanded, setExpanded] = useState({}); // line_id -> bool
  const [showNewLine, setShowNewLine] = useState(false);
  const [tab, setTab] = useState("items"); // items | audit | versions
  // R138bp · Collapsible right rail — persist choice PER ESTIMATE so
  // wide-screen estimators can hide the rail on some estimates but keep
  // it on others (multi-monitor / dual-workflow setups).
  const railKey = `acq_est_rail_collapsed:${eid}`;
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      // Read per-estimate first, fall back to legacy global flag.
      const perEstimate = localStorage.getItem(railKey);
      if (perEstimate !== null) return perEstimate === "1";
      return localStorage.getItem("acq_est_rail_collapsed") === "1";
    } catch { return false; }
  });
  const toggleRail = () => setRailCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem(railKey, nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });
  const [audit, setAudit] = useState([]);
  const [versions, setVersions] = useState([]);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({});
  const [items, setItems] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [commentVis, setCommentVis] = useState("internal");
  const [showApprove, setShowApprove] = useState(null); // 'internal' | 'client' | null
  const [approveNote, setApproveNote] = useState("");
  const [approveContactId, setApproveContactId] = useState("");
  const [clientContacts, setClientContacts] = useState([]);
  const [showEmailPdf, setShowEmailPdf] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showDigestConfig, setShowDigestConfig] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/estimates/${eid}`);
      setEst(data);
      setRatesHidden(!!data._rates_hidden);
    } catch { toast.error("Failed to load estimate"); }
    finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (tab === "audit") api.get(`/estimates/${eid}/audit`).then(({ data }) => setAudit(data || []));
    if (tab === "versions") api.get(`/estimates/${eid}/versions`).then(({ data }) => setVersions(data || []));
  }, [tab, eid]);

  // Load item catalog + comments once
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/items`)
      .then(r => setItems(r.data || [])).catch(() => setItems([]));
    api.get(`/estimates/${eid}/comments`)
      .then(r => setComments(r.data || [])).catch(() => setComments([]));
  }, [eid, activeWorkspaceId]);

  // Client contacts (for on-behalf client approval)
  useEffect(() => {
    if (!est?.client_id) { setClientContacts([]); return; }
    api.get(`/clients/${est.client_id}/contacts`)
      .then(r => setClientContacts(r.data || [])).catch(() => setClientContacts([]));
  }, [est?.client_id]);

  const reloadComments = async () => {
    try {
      const { data } = await api.get(`/estimates/${eid}/comments`);
      setComments(data || []);
    } catch { /* ignore */ }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/estimates/${eid}/comments`, {
        body: newComment.trim(), visibility: commentVis });
      setNewComment("");
      reloadComments();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const doApprove = async (decision) => {
    try {
      const path = showApprove === "internal"
        ? `/estimates/${eid}/approve-internal`
        : `/estimates/${eid}/approve-client`;
      const payload = { decision, note: approveNote || undefined };
      if (showApprove === "client") {
        const c = clientContacts.find((x) => x.id === approveContactId);
        payload.client_contact_id = c?.id;
        payload.client_contact_name = c?.name || c?.email;
      }
      const { data } = await api.post(path, payload);
      setEst(data);
      setShowApprove(null); setApproveNote(""); setApproveContactId("");
      toast.success(`${decision === "approved" ? "Approved" : "Rejected"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const patchLine = async (lid, patch) => {
    try {
      await api.patch(`/estimate-line-items/${lid}`, patch);
      load();
    } catch { toast.error("Failed to update"); }
  };

  const deleteLine = async (lid) => {
    if (!window.confirm("Delete this line?")) return;
    try { await api.delete(`/estimate-line-items/${lid}`); load(); } catch { toast.error("Failed"); }
  };

  const addLine = async (payload) => {
    try {
      await api.post(`/estimates/${eid}/line-items`, payload);
      setShowNewLine(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const addSub = async (lid, payload) => {
    try { await api.post(`/estimate-line-items/${lid}/sub-items`, payload); load(); }
    catch { toast.error("Failed"); }
  };

  const delSub = async (sid) => {
    try { await api.delete(`/estimate-sub-items/${sid}`); load(); } catch { toast.error("Failed"); }
  };

  const submitForApproval = async () => {
    try {
      const { data } = await api.post(`/estimates/${eid}/submit-for-approval`);
      toast.success(`Estimate ${data.status === "approved" ? "auto-approved" : "sent for approval"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  const convertToQuote = async () => {
    try {
      const { data } = await api.post(`/estimates/${eid}/convert-to-quotation`, { expiry_days: 30 });
      toast.success(`Quotation ${data.number} created`);
      // Navigate to existing sales quotation route (if any) — fallback to list
      nav(`/app/sales`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  // Feb 2026 R293zh · Push the approved estimate into the GST Sales
  // module as a Quotation (gst_documents.doc_type == "estimate"). The
  // back-link stamps `source_estimate_id` so the parent estimate shows
  // the resulting QUO- number next time it's opened.
  const pushToGstQuotation = async () => {
    if (!est?.workspace_id) return;
    try {
      const { data } = await api.post(
        `/gst/workspaces/${est.workspace_id}/standalone-estimates/${eid}/to-gst-quotation`,
        {},
      );
      toast.success(data?.reused
        ? `Opened existing quotation ${data.quotation?.doc_no}`
        : `Created quotation ${data.quotation?.doc_no}`);
      nav(`/app/gst-sales?t=estimate`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (!est) return <div className="p-6 text-sm text-muted-foreground">Estimate not found</div>;

  // Iter 224 — always allow edits; post-send edits get snapshotted + audited
  const editable = true;
  const postSend = ["in_review", "approved", "rejected", "converted"].includes(est.status);
  const currency = est.currency || "INR";

  const saveHeader = async () => {
    try {
      await api.patch(`/estimates/${eid}`, headerDraft);
      setEditingHeader(false);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-5" data-testid="estimate-detail-page">
      <div className="flex items-center gap-2 text-xs">
        <Link to="/app/estimates" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> All Estimates
        </Link>
        {est.rfq_id && (
          <Link to={`/app/rfqs/${est.rfq_id}`} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <FileText className="w-3 h-3" /> Source RFQ
          </Link>
        )}
      </div>

      {/* R138aq · Margin threshold alert — flip red banner when
          total_margin_pct falls below the workspace's minimum. */}
      {est.below_min_margin && (
        <div className="border border-rose-400 bg-rose-50 px-3 py-2 flex items-start gap-2 text-xs"
             data-testid="estimate-margin-warning">
          <span className="text-rose-600 font-bold text-base leading-none mt-0.5">⚠</span>
          <div className="flex-1">
            <div className="font-semibold text-rose-800">
              Margin below workspace minimum ({est.min_margin_pct}%)
            </div>
            <div className="text-rose-700 mt-0.5">
              Current margin is <b>{est.total_margin_pct}%</b>. Increase billable rates,
              tighten effort, or reduce discount before sending this estimate to the client.
              To change the workspace threshold, edit <b>Workspace Settings → Estimates → Minimum margin %</b>.
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono text-muted-foreground">{est.number}</span>
            <span className="px-2 py-0.5 border text-[10px] bg-muted capitalize">{est.status?.replace("_", " ")}</span>
            <span className="text-[10px] text-muted-foreground">v{est.version || 1}</span>
            {postSend && (
              <span className="text-[10px] px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-700" title="Edits are still allowed — a snapshot is captured on save">
                edits versioned
              </span>
            )}
          </div>
          {editingHeader ? (
            <div className="mt-2 space-y-2 max-w-xl">
              <input
                value={headerDraft.title ?? est.title}
                onChange={(e) => setHeaderDraft({ ...headerDraft, title: e.target.value })}
                className="w-full text-xl font-bold px-2 py-1 bg-background border border-border"
                data-testid="estimate-edit-title"
              />
              <textarea
                value={headerDraft.description ?? (est.description || "")}
                onChange={(e) => setHeaderDraft({ ...headerDraft, description: e.target.value })}
                placeholder="Description"
                rows={2}
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid="estimate-edit-desc"
              />
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase text-muted-foreground">Global discount %</label>
                <input
                  type="number"
                  step="0.5"
                  value={headerDraft.discount_pct ?? (est.discount_pct || 0)}
                  onChange={(e) => setHeaderDraft({ ...headerDraft, discount_pct: Number(e.target.value) || 0 })}
                  className="text-xs px-2 py-1 bg-background border border-border w-24"
                  data-testid="estimate-edit-discount"
                />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={saveHeader} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1" data-testid="estimate-edit-save">
                  <Check className="w-3 h-3" /> Save
                </button>
                <button type="button" onClick={() => { setEditingHeader(false); setHeaderDraft({}); }} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1">
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <h1 className="text-2xl font-bold">{est.title}</h1>
              <button type="button" onClick={() => setEditingHeader(true)} className="p-1 text-muted-foreground hover:text-foreground" title="Edit" data-testid="estimate-edit-btn">
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-1.5">
            {est.client_name && <span>Client: <b>{est.client_name}</b></span>}
            {est.brand_name && <span className="ml-2">Brand: <b>{est.brand_name}</b></span>}
            {est.last_editor_name && <span className="ml-2">Last edit by <b>{est.last_editor_name}</b></span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {est.status === "draft" && (
            <button type="button" onClick={submitForApproval} className="text-xs px-3 py-1.5 bg-sky-600 text-white hover:opacity-90 flex items-center gap-1.5" data-testid="estimate-submit-approval">
              <Send className="w-3.5 h-3.5" /> Submit for approval
            </button>
          )}
          {est.status === "approved" && (
            <button type="button" onClick={convertToQuote} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="estimate-convert-quote">
              <Sparkles className="w-3.5 h-3.5" /> Convert to Quotation
            </button>
          )}
          {est.status === "approved" && (
            <button type="button" onClick={pushToGstQuotation}
                      className="text-xs px-3 py-1.5 border border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 flex items-center gap-1.5 font-medium"
                      data-testid="estimate-push-gst-quotation">
              <Sparkles className="w-3.5 h-3.5" /> GST Quotation
            </button>
          )}
          {/* R138av · Convert accepted estimate to a Project (copies sub-items to tasks) */}
          {(["approved", "accepted", "converted"].includes(est.status)) && (
            <button type="button"
                     onClick={async () => {
                       const useExisting = est.project_id;
                       let confirmMsg = useExisting
                         ? `Add tasks to the existing linked project?\n\nEach sub-item across all lines will be created as a task with its assignee & est. hours so "actual cost" starts flowing on day one.`
                         : `Create a new project from this estimate?\n\n• Project name: ${est.title || "New Project"}\n• Client & brand: copied from estimate\n• Each sub-item becomes a task with assignee + est. hours`;
                       if (!window.confirm(confirmMsg)) return;
                       try {
                         const { data } = await api.post(`/estimates/${eid}/convert-to-project`, {
                           create_tasks: true,
                         });
                         toast.success(`${data.created_new_project ? "Project created" : "Tasks added"} · ${data.tasks_created} task${data.tasks_created === 1 ? "" : "s"}`);
                         nav(`/app/projects/${data.project_id}`);
                       } catch (e) {
                         toast.error(apiErrorMessage(e, "Convert failed"));
                       }
                     }}
                     className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 flex items-center gap-1.5 font-medium"
                     data-testid="estimate-convert-project">
              <Sparkles className="w-3.5 h-3.5" /> {est.project_id ? "Add tasks to project" : "Convert to Project"}
            </button>
          )}
          <a href={`/api/v1/estimates/${eid}/pdf`} target="_blank" rel="noreferrer"
             className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
             data-testid="est-pdf-btn">
            <Download className="w-3.5 h-3.5" /> PDF
          </a>
          <button type="button" onClick={() => setShowEmailPdf(true)}
                  className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                  data-testid="est-email-btn">
            <Mail className="w-3.5 h-3.5" /> Email PDF
          </button>
          <button type="button" onClick={async () => { const { data } = await api.post(`/estimates/${eid}/duplicate`); toast.success(`Duplicated as ${data.number}`); nav(`/app/estimates/${data.id}`); }} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5">
            <Copy className="w-3.5 h-3.5" /> Duplicate
          </button>
          <button type="button" onClick={() => setShowSaveTemplate(true)}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                   data-testid="est-save-template-btn">
            <FileText className="w-3.5 h-3.5" /> Save as template
          </button>
          <button type="button" onClick={() => setShowDigestConfig(true)}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5"
                   data-testid="est-digest-config-btn">
            <Mail className="w-3.5 h-3.5" /> Effort digest
          </button>
        </div>
      </div>

      {/* Iter 248 · Rates-hidden callout suppressed per user request — "people
          don't need to know what they don't know". `ratesHidden` still gates
          the rate/tax/total columns silently. */}

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {[
          { k: "items", label: "Line items" },
          { k: "audit", label: `Audit trail${audit.length ? ` (${audit.length})` : ""}` },
          { k: "versions", label: `Versions${versions.length ? ` (${versions.length})` : ""}` },
        ].map((t) => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)} className={`text-xs px-3 py-2 border-b-2 -mb-px ${tab === t.k ? "border-primary text-foreground font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`estimate-tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* 2-col body — R138bo · right rail collapses to maximise line grid */}
      <div className={railCollapsed
            ? "grid grid-cols-1 gap-5 relative"
            : "grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 relative"}>
        <div className="min-w-0 space-y-4">
          {/* R138bo · Floating collapse/expand toggle */}
          <button
            type="button"
            onClick={toggleRail}
            className="absolute right-0 -top-9 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-border bg-background hover:bg-muted"
            title={railCollapsed ? "Show details panel" : "Hide details panel"}
            data-testid="estimate-rail-toggle"
          >
            {railCollapsed
              ? <><PanelRightOpen className="w-3 h-3" /> Details</>
              : <><PanelRightClose className="w-3 h-3" /> Hide details</>}
          </button>

      {tab === "items" && (
      <ExcelEstimateLines
        est={est}
        items={items}
        currency={currency}
        ratesHidden={ratesHidden}
        canViewEffort={est?._can_view_effort !== false}
        onSaved={load}
      />
      )}

      {tab === "audit" && (
        <EstAuditTrail rows={audit} />
      )}

      {tab === "versions" && (
        <EstVersionsPanel rows={versions} est={est} onRestored={load} />
      )}
        </div>
        {/* Right rail — hidden when collapsed */}
        {!railCollapsed && (
        <aside className="space-y-4" data-testid="est-rail">
          <EstContextCard est={est} />

          <EstApprovalCard title="Internal approval"
            status={est.internal_approval_status}
            byName={est.internal_approval_by_name}
            at={est.internal_approval_at}
            note={est.internal_approval_note}
            onDecide={() => { setShowApprove("internal"); setApproveNote(""); }}
            testidRoot="est-approval-internal"
          />

          <EstApprovalCard title="Client approval"
            status={est.client_approval_status}
            byName={est.client_approval_by_name}
            at={est.client_approval_at}
            note={est.client_approval_note}
            recordedByName={est.client_approval_recorded_by_name}
            onDecide={() => { setShowApprove("client"); setApproveNote(""); setApproveContactId(""); }}
            testidRoot="est-approval-client"
          />

          <EstAttachmentsPanel est={est} wsId={activeWorkspaceId} onChanged={load} />

          <EstCommentsPanel comments={comments}
            newComment={newComment} setNewComment={setNewComment}
            commentVis={commentVis} setCommentVis={setCommentVis}
            onAdd={addComment}
          />
        </aside>
        )}
      </div>

      {/* Approve modal */}
      {showApprove && (
        <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowApprove(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl" data-testid={`est-approve-dialog-${showApprove}`}>
            <div className="px-4 py-3 border-b border-border font-semibold text-sm">
              {showApprove === "internal" ? "Internal approval" : "Record client decision"}
            </div>
            <div className="p-4 space-y-3">
              {showApprove === "client" && (
                <div>
                  <label className="text-[10px] uppercase text-muted-foreground">Client contact</label>
                  <SearchableSelect
                    value={approveContactId} onValueChange={setApproveContactId}
                    options={[{value:"",label:"—"},...clientContacts.map(c=>({value:c.id,label:c.name||c.email,hint:c.email}))]}
                    placeholder="Search contact…"
                    testid="est-approve-contact"
                  />
                </div>
              )}
              <div>
                <label className="text-[10px] uppercase text-muted-foreground">Note (optional)</label>
                <textarea rows={3} value={approveNote}
                  onChange={(e) => setApproveNote(e.target.value)}
                  className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                  data-testid="est-approve-note" />
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
              <button type="button" onClick={() => setShowApprove(null)} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
              <button type="button" onClick={() => doApprove("rejected")} className="text-xs px-3 py-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="est-approve-reject">Reject</button>
              <button type="button" onClick={() => doApprove("approved")} className="text-xs px-3 py-1.5 bg-emerald-600 text-white hover:opacity-90 font-medium" data-testid="est-approve-approve">Approve</button>
            </div>
          </div>
        </div>
      )}

      {showEmailPdf && (
        <EstEmailPdfDialog est={est} clientContacts={clientContacts}
                           onClose={() => setShowEmailPdf(false)}
                           onSent={load} />
      )}

      {showSaveTemplate && (
        <SaveTemplateDialog est={est}
          onClose={() => setShowSaveTemplate(false)}
          onSaved={() => { setShowSaveTemplate(false); toast.success("Template saved"); }} />
      )}

      {showDigestConfig && (
        <EffortDigestDialog est={est}
          onClose={() => setShowDigestConfig(false)}
          onSaved={load} />
      )}
    </div>
  );
}

function EstAuditTrail({ rows }) {
  if (!rows || rows.length === 0) {
    return <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="estimate-audit-empty">No audit events yet.</div>;
  }
  return (
    <div className="border border-border divide-y divide-border" data-testid="estimate-audit-trail">
      {rows.map((r) => (
        <div key={r.id} className="p-3 text-xs" data-testid={`estimate-audit-row-${r.id}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 text-[10px] border ${r.action?.includes("post_send") ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}>{r.action}</span>
              <span className="font-medium">{r.actor_name || r.actor_id}</span>
            </div>
            <span className="text-muted-foreground">{fmtRelative(r.at)}</span>
          </div>
          {r.changes && Object.keys(r.changes).length > 0 && (
            <div className="mt-2 pl-2 border-l-2 border-border space-y-0.5">
              {Object.entries(r.changes).map(([k, v]) => (
                <div key={k} className="text-[11px]">
                  <span className="font-mono text-muted-foreground">{k}</span>
                  {v && typeof v === "object" && "from" in v ? (
                    <span> · <span className="line-through text-rose-600">{fmtVal(v.from)}</span> → <span className="text-emerald-700">{fmtVal(v.to)}</span></span>
                  ) : (
                    <span>: {fmtVal(v)}</span>
                  )}
                </div>
              ))}
            </div>
          )}
          {r.version_snapshot_id && (
            <div className="mt-1 text-[10px] text-muted-foreground">snapshot: <span className="font-mono">{r.version_snapshot_id.slice(0, 8)}</span></div>
          )}
        </div>
      ))}
    </div>
  );
}

function fmtVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function EstVersionsPanel({ rows, est, onRestored }) {
  const [diffing, setDiffing] = useState(null);
  if (!rows || rows.length === 0) {
    return <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground" data-testid="estimate-versions-empty">
      No prior versions. A snapshot is captured automatically when the estimate is edited after being sent/approved.
    </div>;
  }
  const restore = async () => {
    await api.post(`/estimates/${est.id}/versions/${diffing.id}/restore`);
    onRestored();
  };
  return (
    <>
      <div className="border border-border divide-y divide-border" data-testid="estimate-versions-list">
        {rows.map((v) => (
          <div key={v.id} className="p-3 text-xs flex items-center justify-between hover:bg-muted/30 cursor-pointer" onClick={() => setDiffing(v)} data-testid={`estimate-version-row-${v.id}`}>
            <div className="flex items-center gap-2">
              <History className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="font-semibold">v{v.version}</span>
              <span className="text-muted-foreground">· {v.line_items?.length || 0} line{v.line_items?.length === 1 ? "" : "s"}</span>
              {v.snapshot?.grand_total !== undefined && !v.snapshot?._rates_hidden && (
                <span className="text-muted-foreground">· grand ₹{Number(v.snapshot.grand_total).toLocaleString("en-IN")}</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={(e) => { e.stopPropagation(); setDiffing(v); }} className="text-primary hover:underline text-[11px] inline-flex items-center gap-1" data-testid={`estimate-version-diff-${v.id}`}>
                <ArrowLeftRight className="w-3 h-3" /> Compare with current
              </button>
              <span className="text-muted-foreground">{fmtRelative(v.created_at)}</span>
            </div>
          </div>
        ))}
      </div>
      {diffing && est && (
        <DocDiffDialog
          version={diffing}
          current={est}
          currentLines={est.line_items || []}
          fields={[
            { key: "title", label: "Title" },
            { key: "description", label: "Description" },
            { key: "status", label: "Status" },
            { key: "discount_pct", label: "Global discount", kind: "pct" },
            { key: "grand_total", label: "Grand total", kind: "money" },
            { key: "subtotal", label: "Subtotal", kind: "money" },
            { key: "tax_amount", label: "Tax", kind: "money" },
          ]}
          lineFields={[
            { key: "name", label: "Name" },
            { key: "qty", label: "Qty" },
            { key: "unit", label: "Unit" },
            { key: "applied_rate", label: "Rate", kind: "money" },
            { key: "tax_pct", label: "Tax %", kind: "pct" },
            { key: "discount_pct", label: "Disc %", kind: "pct" },
          ]}
          onRestore={restore}
          onClose={() => setDiffing(null)}
          title={`Estimate ${est.number} · Version diff`}
        />
      )}
    </>
  );
}

function LineRow({ li, idx, editable, ratesHidden, currency, expanded, onToggle, onPatch, onDelete, onAddSub, onDelSub, wsId, clientId, brandId }) {
  const [rateLookup, setRateLookup] = useState(null);
  const [showAddSub, setShowAddSub] = useState(false);

  const loadRates = async () => {
    try {
      const params = { client_id: clientId, brand_id: brandId };
      if (li.catalog_item_id) params.catalog_item_id = li.catalog_item_id;
      const { data } = await api.get(`/workspaces/${wsId}/rate-lookup`, { params });
      setRateLookup(data);
    } catch { /* silent */ }
  };

  return (
    <div className="p-3 hover:bg-muted/20" data-testid={`estimate-line-${li.id}`}>
      <div className="flex items-start gap-2">
        <button type="button" onClick={onToggle} className="mt-0.5 p-0.5 text-muted-foreground hover:text-foreground">
          {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <span className="text-[10px] text-muted-foreground mt-1 w-4 shrink-0">{idx}</span>
        <div className="flex-1 min-w-0 grid grid-cols-12 gap-2 items-start">
          <div className="col-span-5">
            {editable ? (
              <input value={li.name} onChange={(e) => onPatch({ name: e.target.value })} className="w-full text-sm px-2 py-1 bg-background border border-border font-medium" />
            ) : <div className="text-sm font-medium py-1">{li.name}</div>}
            {li.description && <div className="text-[10px] text-muted-foreground mt-0.5">{li.description}</div>}
          </div>
          <div className="col-span-1">
            {editable ? (
              <input type="number" step="0.5" value={li.qty} onChange={(e) => onPatch({ qty: Number(e.target.value) || 0 })} className="w-full text-xs px-2 py-1 bg-background border border-border" />
            ) : <div className="text-xs py-1">{li.qty}</div>}
          </div>
          <div className="col-span-1 text-xs py-1">{li.unit}</div>
          {!ratesHidden && (
            <>
              <div className="col-span-2">
                {editable ? (
                  <div className="flex items-center gap-1">
                    <input type="number" step="1" value={li.applied_rate || 0} onChange={(e) => onPatch({ applied_rate: Number(e.target.value) || 0, rate_source: "custom" })} className="w-full text-xs px-2 py-1 bg-background border border-border" data-testid={`estimate-line-rate-${li.id}`} />
                  </div>
                ) : <div className="text-xs py-1 font-mono">{fmtMoney(li.applied_rate, currency)}</div>}
                {li.rate_source && (
                  <span className={`inline-block mt-1 px-1.5 py-0.5 text-[9px] border ${RATE_SOURCE_STYLE[li.rate_source] || RATE_SOURCE_STYLE.custom}`}>
                    {RATE_SOURCE_LABEL[li.rate_source] || li.rate_source}
                  </span>
                )}
              </div>
              <div className="col-span-1">
                {editable ? (
                  <input type="number" step="0.5" value={li.tax_pct || 0} onChange={(e) => onPatch({ tax_pct: Number(e.target.value) || 0 })} className="w-full text-xs px-2 py-1 bg-background border border-border" title="Tax %" />
                ) : <div className="text-xs py-1">{li.tax_pct || 0}%</div>}
              </div>
              <div className="col-span-1">
                {editable ? (
                  <input type="number" step="1" value={li.discount_pct || 0} onChange={(e) => onPatch({ discount_pct: Number(e.target.value) || 0 })} className="w-full text-xs px-2 py-1 bg-background border border-border" title="Discount %" />
                ) : <div className="text-xs py-1">{li.discount_pct || 0}%</div>}
              </div>
              <div className="col-span-1 text-right text-xs py-1 font-mono font-medium">
                {fmtMoney(li.amount_after_tax, currency)}
              </div>
            </>
          )}
          <div className="col-span-1 text-right">
            {editable && (
              <button type="button" onClick={onDelete} className="p-1 text-muted-foreground hover:text-rose-500" data-testid={`estimate-line-delete-${li.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-8 pl-4 border-l-2 border-border space-y-3">
          {!ratesHidden && editable && (
            <div>
              {!rateLookup ? (
                <button type="button" onClick={loadRates} className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1" data-testid={`estimate-line-lookup-${li.id}`}>
                  <Sparkles className="w-3 h-3" /> Show rate options
                </button>
              ) : (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Rate options:</span>
                  {["client_price_list", "brand_rate_card", "catalog", "designation_rate"].map((k) => {
                    const r = rateLookup[k];
                    if (!r) return null;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => onPatch({ applied_rate: r.rate, rate_source: k, base_rate: r.rate })}
                        className={`px-2 py-1 border text-[10px] hover:opacity-90 ${RATE_SOURCE_STYLE[k]}`}
                        data-testid={`estimate-line-pick-${k}-${li.id}`}
                      >
                        <b>{RATE_SOURCE_LABEL[k]}</b>: {fmtMoney(r.rate, currency)}
                      </button>
                    );
                  })}
                  {rateLookup.suggested_source && (
                    <span className="text-[10px] text-muted-foreground">
                      · Suggested: <b>{RATE_SOURCE_LABEL[rateLookup.suggested_source]}</b>
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sub-items */}
          <div>
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide mb-1">Sub-items (effort break-down)</div>
            <div className="border border-border">
              <div className="grid grid-cols-12 gap-2 px-2 py-1.5 text-[10px] uppercase text-muted-foreground bg-muted/30 border-b border-border">
                <div className="col-span-4">Name</div>
                <div className="col-span-2">Designation</div>
                <div className="col-span-1">Hrs</div>
                {!ratesHidden && (<><div className="col-span-2">Rate/hr</div><div className="col-span-2 text-right">Cost</div></>)}
                <div className="col-span-1"></div>
              </div>
              {(li.sub_items || []).map((s) => (
                <div key={s.id} className="grid grid-cols-12 gap-2 px-2 py-1.5 text-xs border-b border-border last:border-0" data-testid={`estimate-sub-${s.id}`}>
                  <div className="col-span-4">{s.name}</div>
                  <div className="col-span-2 text-muted-foreground">{s.designation || "—"}</div>
                  <div className="col-span-1">{s.hours}</div>
                  {!ratesHidden && (
                    <>
                      <div className="col-span-2 font-mono">{fmtMoney(s.hourly_rate, currency)}</div>
                      <div className="col-span-2 text-right font-mono">{fmtMoney(s.cost, currency)}</div>
                    </>
                  )}
                  <div className="col-span-1 text-right">
                    {editable && (
                      <button type="button" onClick={() => onDelSub(s.id)} className="p-0.5 text-muted-foreground hover:text-rose-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {showAddSub && editable && <AddSubForm onSave={(p) => { onAddSub(p); setShowAddSub(false); }} onCancel={() => setShowAddSub(false)} />}
              {editable && !showAddSub && (
                <button type="button" onClick={() => setShowAddSub(true)} className="w-full text-[10px] py-1.5 text-muted-foreground hover:bg-muted flex items-center justify-center gap-1" data-testid={`estimate-sub-add-${li.id}`}>
                  <Plus className="w-3 h-3" /> Add sub-item
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewLineForm({ onSave, onCancel }) {
  const [name, setName] = useState("");
  const [qty, setQty] = useState(1);
  const [unit, setUnit] = useState("unit");
  const [rate, setRate] = useState(0);
  const [tax, setTax] = useState(0);
  return (
    <div className="p-3 bg-muted/20 grid grid-cols-12 gap-2 items-center">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Line item name" className="col-span-5 text-sm px-2 py-1 bg-background border border-border" autoFocus data-testid="estimate-newline-name" />
      <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty" className="col-span-1 text-xs px-2 py-1 bg-background border border-border" />
      <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="unit" className="col-span-1 text-xs px-2 py-1 bg-background border border-border" />
      <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate" className="col-span-2 text-xs px-2 py-1 bg-background border border-border" />
      <input type="number" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="Tax %" className="col-span-1 text-xs px-2 py-1 bg-background border border-border" />
      <button type="button" onClick={() => onSave({ name, qty: Number(qty), unit, applied_rate: Number(rate), base_rate: Number(rate), tax_pct: Number(tax), rate_source: "custom" })} disabled={!name.trim()} className="col-span-1 text-xs px-2 py-1 bg-primary text-primary-foreground disabled:opacity-50" data-testid="estimate-newline-save">Add</button>
      <button type="button" onClick={onCancel} className="col-span-1 text-xs px-2 py-1 border border-border">Cancel</button>
    </div>
  );
}

function AddSubForm({ onSave, onCancel }) {
  const [name, setName] = useState("");
  const [designation, setDesignation] = useState("");
  const [hours, setHours] = useState(1);
  const [rate, setRate] = useState(0);
  return (
    <div className="grid grid-cols-12 gap-2 px-2 py-1.5 text-xs border-t border-border bg-muted/10 items-center">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Sub-item name" className="col-span-4 px-2 py-1 bg-background border border-border" autoFocus />
      <input value={designation} onChange={(e) => setDesignation(e.target.value)} placeholder="Designer" className="col-span-2 px-2 py-1 bg-background border border-border" />
      <input type="number" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} className="col-span-1 px-2 py-1 bg-background border border-border" />
      <input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Rate/hr" className="col-span-2 px-2 py-1 bg-background border border-border" />
      <div className="col-span-2 text-right font-mono text-muted-foreground">≈ ₹{(Number(hours) * Number(rate) || 0).toLocaleString("en-IN")}</div>
      <div className="col-span-1 text-right space-x-1">
        <button type="button" onClick={() => onSave({ name, designation, hours: Number(hours), hourly_rate: Number(rate), rate_source: "custom" })} disabled={!name.trim()} className="text-[10px] px-1.5 py-0.5 bg-primary text-primary-foreground disabled:opacity-50">Add</button>
        <button type="button" onClick={onCancel} className="text-[10px] px-1.5 py-0.5 border border-border">×</button>
      </div>
    </div>
  );
}


/* ─────────────────────────── Iter 238 helpers ─────────────────────────── */

function EstContextCard({ est }) {
  return (
    <div className="border border-border p-3" data-testid="est-context-card">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
        <Building2 className="w-3 h-3" /> Context
      </div>
      {est.client_name && (
        <div className="text-xs mb-1 flex items-center gap-1.5">
          <Building2 className="w-3 h-3 text-muted-foreground" />
          <b>{est.client_name}</b>
          {est.client_code && <span className="text-[10px] text-muted-foreground">({est.client_code})</span>}
        </div>
      )}
      {est.brand_name && (
        <div className="text-xs mb-1 flex items-center gap-1.5">
          <Briefcase className="w-3 h-3 text-muted-foreground" /> {est.brand_name}
        </div>
      )}
      {est.project_name && (
        <div className="text-xs mb-1 flex items-center gap-1.5">
          <FolderKanban className="w-3 h-3 text-muted-foreground" />
          <Link to={`/app/projects/${est.project_id}`} className="text-primary hover:underline">{est.project_name}</Link>
        </div>
      )}
      {est.rfq_number && (
        <div className="text-xs flex items-center gap-1.5">
          <FileText className="w-3 h-3 text-muted-foreground" />
          <Link to={`/app/rfqs/${est.rfq_id}`} className="text-primary hover:underline">
            {est.rfq_number} · {est.rfq_title || "RFQ"}
          </Link>
        </div>
      )}
      {!est.client_name && !est.brand_name && !est.project_name && !est.rfq_number && (
        <div className="text-[11px] text-muted-foreground italic">No links.</div>
      )}
    </div>
  );
}


function EstApprovalCard({ title, status, byName, at, note, recordedByName, onDecide, testidRoot }) {
  const cls = status === "approved" ? "text-emerald-700 border-emerald-300 bg-emerald-50"
            : status === "rejected" ? "text-rose-700 border-rose-300 bg-rose-50"
            : "text-slate-700 border-slate-300 bg-slate-50";
  const Icon = status === "approved" ? CheckCircle2 : status === "rejected" ? XCircle : ShieldCheck;
  return (
    <div className="border border-border p-3" data-testid={`${testidRoot}-card`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <ShieldCheck className="w-3 h-3" /> {title}
        </div>
        <button type="button" onClick={onDecide} className="text-[11px] px-2 py-0.5 border border-border hover:bg-muted" data-testid={`${testidRoot}-decide`}>
          {status && status !== "pending" ? "Re-decide" : "Decide"}
        </button>
      </div>
      <div className={`flex items-center gap-2 px-2 py-1 border ${cls} text-[11px]`}>
        <Icon className="w-3 h-3" />
        <span className="capitalize font-medium">{status || "pending"}</span>
      </div>
      {byName && (
        <div className="text-[11px] text-muted-foreground mt-2">
          <span>By <b className="text-foreground">{byName}</b></span>
          {at && <span> · {fmtRelative(at)}</span>}
          {recordedByName && <span className="ml-1 italic">(recorded by {recordedByName})</span>}
        </div>
      )}
      {note && (
        <div className="text-[11px] text-muted-foreground mt-1 border-l-2 border-border pl-2">
          {note}
        </div>
      )}
    </div>
  );
}


function EstAttachmentsPanel({ est, wsId, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const attachments = est.attachments || [];
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("category", "estimate_attachment");
      const { data } = await api.post(`/workspaces/${wsId}/files/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" }});
      await api.post(`/estimates/${est.id}/attachments`, {
        file_id: data.id, filename: data.filename || file.name,
        content_type: data.content_type, size: data.size, url: data.url,
      });
      onChanged();
      toast.success("Attached");
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (fid) => {
    try { await api.delete(`/estimates/${est.id}/attachments/${fid}`); onChanged(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  return (
    <div className="border border-border p-3" data-testid="est-attachments-card">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
          <Paperclip className="w-3 h-3" /> Attachments ({attachments.length})
        </div>
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
                className="text-[11px] px-2 py-0.5 border border-border hover:bg-muted disabled:opacity-50"
                data-testid="est-attach-btn">
          {busy ? "…" : "+ Add"}
        </button>
        <input ref={fileRef} type="file" className="hidden"
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="est-attach-input" />
      </div>
      {attachments.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">Carried from RFQ or add your own.</div>
      ) : (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li key={a.file_id} className="flex items-center gap-2 text-[11px] border border-border p-1.5"
                data-testid={`est-attach-${a.file_id}`}>
              <Paperclip className="w-3 h-3 text-muted-foreground shrink-0" />
              <a href={a.url || `/api/v1/files/${a.file_id}`} target="_blank" rel="noreferrer"
                 className="truncate hover:underline flex-1">{a.filename}</a>
              <button type="button" onClick={() => remove(a.file_id)}
                       className="text-muted-foreground hover:text-rose-500"
                       aria-label="Remove"><Trash2 className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function EstCommentsPanel({ comments, newComment, setNewComment, commentVis, setCommentVis, onAdd }) {
  return (
    <div className="border border-border p-3" data-testid="est-comments-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
        <MessageSquare className="w-3 h-3" /> Activity ({comments.length})
      </div>
      <div className="max-h-56 overflow-y-auto space-y-2 mb-2">
        {comments.length === 0 && (
          <div className="text-[11px] text-muted-foreground italic">No comments yet.</div>
        )}
        {comments.map((c) => (
          <div key={c.id} className="border border-border p-2" data-testid={`est-comment-${c.id}`}>
            <div className="flex items-center gap-2 text-[10px]">
              <span className="font-medium text-foreground">{c.author_name}</span>
              <span className="text-muted-foreground">· {fmtRelative(c.created_at)}</span>
              {c.visibility === "client" && (
                <span className="px-1 border border-indigo-300 bg-indigo-50 text-indigo-700 text-[10px]">Client-visible</span>
              )}
            </div>
            <div className="text-xs mt-1 whitespace-pre-wrap">{c.body}</div>
          </div>
        ))}
      </div>
      <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a note or reply…" rows={2}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); onAdd(); } }}
                className="w-full text-xs px-2 py-1 bg-background border border-border"
                data-testid="est-comment-input" />
      <div className="flex items-center justify-between mt-1">
        <select value={commentVis} onChange={(e) => setCommentVis(e.target.value)}
                className="text-[11px] px-1 py-0.5 bg-background border border-border">
          <option value="internal">Internal</option>
          <option value="client">Client-visible</option>
        </select>
        <button type="button" onClick={onAdd} disabled={!newComment.trim()}
                className="text-[11px] px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium"
                data-testid="est-comment-post">
          Post <span className="text-[9px] opacity-70 ml-1">⌘⏎</span>
        </button>
      </div>
    </div>
  );
}


// R138bp · Feb 2026 — Estimate-level Scenario Compare Rollup.
// Aggregates each of the 4 cost-source scenarios (Designation / Custom /
// User salary / Actual) across ALL lines in this estimate, then renders
// a compact 4-column card so the estimator can see which cost source
// gives the best overall margin — not just per line.
function EstimateScenarioRollup({ items, designations, members, currency, csym, wsId, estimateId, onSaved }) {
  const desMap = useMemo(() => {
    const m = {};
    (designations || []).forEach((d) => { m[d.id] = d; });
    return m;
  }, [designations]);
  const userHourlyMap = useMemo(() => {
    const m = {};
    (members || []).forEach((mem) => {
      const uid = mem.user_id || mem.id;
      const sal = Number(mem.salary_amount || mem.salary || 0);
      if (uid && sal > 0) m[uid] = sal / (22 * 8);
    });
    return m;
  }, [members]);

  const { scenarios, revenueTotal, hasSubItems, currentCost } = useMemo(() => {
    let revenueTotal = 0;
    let dCost = 0, cCost = 0, uCost = 0, aCost = 0;
    let dCov = 0, cCov = 0, uCov = 0, aCov = 0;
    let curCost = 0;
    let totalHours = 0;
    let hasSubItems = false;
    (items || []).forEach((li) => {
      const rev = Number(li.effort_amount_total ?? li.applied_rate * (li.qty || 1) ?? 0);
      revenueTotal += rev;
      const subs = li.sub_items || [];
      if (subs.length > 0) hasSubItems = true;
      subs.forEach((s) => {
        const h = Number(s.hours) || 0;
        totalHours += h;
        const dRate = s.designation_id && desMap[s.designation_id]
          ? Number(desMap[s.designation_id].default_hourly_rate || 0)
          : Number(s.hourly_rate || 0);
        if (dRate > 0) { dCost += h * dRate; dCov += h; }
        const cRate = Number(s.custom_internal_rate || 0);
        if (cRate > 0) { cCost += h * cRate; cCov += h; }
        const uRate = s.assigned_user_id ? (userHourlyMap[s.assigned_user_id] || 0) : 0;
        if (uRate > 0) { uCost += h * uRate; uCov += h; }
        const aH = Number(s.actual_hours || 0);
        const aR = Number(s.actual_effective_rate || 0);
        if (aH > 0 && aR > 0) { aCost += aH * aR; aCov += aH; }
        // R138bu · "Current" cost — uses the row's persisted cost_source
        // so the compare-delta arrow reflects real savings/losses vs
        // the option currently in force.
        const src = (s.cost_source || "designation").toLowerCase();
        let rowCost = 0;
        if (src === "custom" && cRate > 0) rowCost = h * cRate;
        else if (src === "user" && uRate > 0) rowCost = h * uRate;
        else if (src === "actual" && aH > 0 && aR > 0) rowCost = aH * aR;
        else if (dRate > 0) rowCost = h * dRate;
        curCost += rowCost;
      });
    });

    const mk = (key, label, cost, cov) => {
      const covered = totalHours > 0 && cov >= totalHours * 0.95;
      const partial = cov > 0 && !covered;
      const missing = cov === 0;
      const margin = revenueTotal - cost;
      const marginPct = revenueTotal > 0 ? (margin / revenueTotal) * 100 : 0;
      return { key, label, cost, margin, marginPct, covered, partial, missing };
    };
    return {
      scenarios: [
        mk("designation", "Designation", dCost, dCov),
        mk("custom",      "Custom",      cCost, cCov),
        mk("user",        "User salary", uCost, uCov),
        mk("actual",      "Actual",      aCost, aCov),
      ],
      revenueTotal,
      hasSubItems,
      currentCost: curCost,
    };
  }, [items, desMap, userHourlyMap]);

  const bestKey = useMemo(() => {
    const valid = scenarios.filter((s) => !s.missing);
    if (!valid.length) return null;
    return valid.reduce((b, s) => (s.marginPct > b.marginPct ? s : b), valid[0]).key;
  }, [scenarios]);

  // R138bu · Estimate-level Auto-Apply — bulk-flip cost_source on every
  // sub_item across ALL lines in this estimate.
  const [applying, setApplying] = useState(false);
  const applyToAll = async (sourceKey) => {
    if (!estimateId || !wsId) return;
    if (!window.confirm(`Apply "${sourceKey.replace(/_/g, " ")}" cost basis to every effort row across all lines? Rows without the required data (e.g. no assigned user for User salary) will be skipped.`)) return;
    setApplying(true);
    try {
      const patches = [];
      (items || []).forEach((li) => {
        (li.sub_items || []).forEach((s) => {
          if (sourceKey === "user" && !s.assigned_user_id) return;
          if (sourceKey === "custom" && !s.custom_internal_rate) return;
          if (sourceKey === "actual" && !(s.actual_hours && s.actual_effective_rate)) return;
          patches.push({ line_id: li.id, sub_id: s.id, source: sourceKey });
        });
      });
      // Sequential PATCH per sub_item — small volumes, keeps backend simple.
      for (const p of patches) {
        try {
          await api.patch(
            `/estimates/${estimateId}/items/${p.line_id}/sub-items/${p.sub_id}`,
            { cost_source: p.source });
        } catch { /* skip failures */ }
      }
      toast.success(`${patches.length} rows switched to "${sourceKey.replace(/_/g, " ")}" cost basis.`);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Bulk apply failed"));
    } finally { setApplying(false); }
  };

  if (!hasSubItems) return null;

  const fmt = (v) => `${csym}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="border-t-2 border-border bg-primary/5"
         data-testid="estimate-scenario-rollup">
      <div className="px-3 py-2 border-b border-border bg-primary/10 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
          Estimate-level scenario compare · all lines
        </span>
        <span className="text-[10px] text-muted-foreground">
          Revenue total · <span className="font-mono tabular-nums font-semibold text-foreground">{fmt(revenueTotal)}</span>
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4">
        {scenarios.map((s) => {
          const isBest = s.key === bestKey && !s.missing;
          const tone = s.missing
            ? "bg-muted/20 opacity-60"
            : isBest
              ? "bg-emerald-100 border-l-2 border-emerald-600"
              : s.marginPct < 20
                ? "bg-rose-50"
                : "";
          // R138bu · Compare Delta — how much margin we'd GAIN (or lose)
          // by switching every row to this source vs the currently-
          // applied source mix.
          const delta = !s.missing ? (currentCost - s.cost) : 0;
          const deltaPct = revenueTotal > 0 ? (delta / revenueTotal) * 100 : 0;
          const isClickable = !s.missing && !!estimateId;
          const Wrapper = isClickable ? "button" : "div";
          return (
            <Wrapper key={s.key}
                 type={isClickable ? "button" : undefined}
                 disabled={applying}
                 onClick={isClickable ? () => applyToAll(s.key) : undefined}
                 title={isClickable ? `Apply "${s.label}" to every row across all lines` : undefined}
                 className={`text-left w-full px-3 py-2 border-r last:border-r-0 border-border tabular-nums transition ${tone} ${isClickable ? "hover:bg-primary/10 cursor-pointer disabled:opacity-60" : ""}`}
                 data-testid={`estimate-scenario-${s.key}`}
                 data-margin-pct={s.marginPct.toFixed(2)}
                 data-delta={delta.toFixed(2)}
                 data-is-best={isBest ? "true" : "false"}>
              <div className="flex items-baseline justify-between gap-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {s.label}
                </span>
                {isBest && (
                  <span className="text-[9px] uppercase tracking-widest bg-emerald-600 text-white px-1 py-0.5">
                    Overall best
                  </span>
                )}
                {s.partial && !isBest && (
                  <span className="text-[9px] uppercase tracking-widest text-amber-700">Partial</span>
                )}
              </div>
              {s.missing ? (
                <div className="text-[11px] text-muted-foreground italic mt-1">
                  {s.key === "actual" ? "Needs project link + timesheet" : "No data yet"}
                </div>
              ) : (
                <>
                  <div className="text-[11px] text-muted-foreground mt-1 flex justify-between">
                    <span>Cost</span><span className="font-mono">{fmt(s.cost)}</span>
                  </div>
                  <div className="text-sm font-semibold flex justify-between mt-0.5">
                    <span>Margin</span>
                    <span className={`font-mono ${s.margin < 0 ? "text-rose-700" : s.marginPct < 20 ? "text-amber-700" : "text-emerald-700"}`}>
                      {fmt(s.margin)}
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground flex justify-between">
                    <span>Margin %</span>
                    <span className={`font-mono font-bold ${s.marginPct < 0 ? "text-rose-700" : s.marginPct < 20 ? "text-amber-700" : "text-emerald-700"}`}>
                      {s.marginPct.toFixed(1)}%
                    </span>
                  </div>
                  {/* R138bu · Delta vs current selection */}
                  {Math.abs(delta) > 0.5 && (
                    <div className={`mt-1 flex items-baseline justify-between text-[10px] font-mono ${delta > 0 ? "text-emerald-700" : "text-rose-700"}`}
                          data-testid={`estimate-scenario-${s.key}-delta`}>
                      <span className="uppercase tracking-widest opacity-70">vs current</span>
                      <span className="font-bold">
                        {delta > 0 ? "▲ +" : "▼ "}{fmt(Math.abs(delta))}
                        <span className="opacity-70 ml-1">({delta >= 0 ? "+" : ""}{deltaPct.toFixed(1)}%)</span>
                      </span>
                    </div>
                  )}
                  {isClickable && (
                    <div className="mt-1 text-[9px] uppercase tracking-widest text-primary/70">
                      {applying ? "Applying…" : "Click to apply to all lines →"}
                    </div>
                  )}
                </>
              )}
            </Wrapper>
          );
        })}
      </div>
    </div>
  );
}


function ExcelEstimateLines({ est, items: itemsProp, currency, ratesHidden, canViewEffort, onSaved }) {
  const [items, setItems] = useState(itemsProp || []);
  const [designations, setDesignations] = useState([]);
  const [members, setMembers] = useState([]);  // R138ap · for user-cost picker in EffortBuilder
  const [showDesignations, setShowDesignations] = useState(false);
  const [expanded, setExpanded] = useState({}); // idx -> bool
  useEffect(() => { setItems(itemsProp || []); }, [itemsProp]);

  const loadDesignations = () => {
    if (!est.workspace_id) return;
    api.get(`/workspaces/${est.workspace_id}/estimate-designations`, { params: { active_only: false } })
      .then(({ data }) => setDesignations(data || []))
      .catch(() => setDesignations([]));
  };
  useEffect(() => { loadDesignations(); }, [est.workspace_id]); // eslint-disable-line
  useEffect(() => {
    if (!est.workspace_id) return;
    api.get(`/workspaces/${est.workspace_id}/members`)
      .then(({ data }) => setMembers(Array.isArray(data) ? data : (data?.members || [])))
      .catch(() => setMembers([]));
  }, [est.workspace_id]);

  const csym = currency === "INR" ? "₹" : currency + " ";
  const buildRows = (data) => (data.line_items || []).map((li, i) => ({
    id: li.id, name: li.name || "", description: li.description || "",
    qty: li.qty || 1, unit: li.unit || "unit",
    applied_rate: li.applied_rate ?? li.base_rate ?? 0,
    tax_pct: li.tax_pct || 0, discount_pct: li.discount_pct || 0,
    note: li.note || "", rfq_note: li.rfq_note || null,
    catalog_item_id: li.catalog_item_id, estimated_hours: li.estimated_hours,
    effort_mode: li.effort_mode || "manual",
    effort_hours_total: li.effort_hours_total,
    effort_amount_total: li.effort_amount_total,
    effort_hours_per_unit: li.effort_hours_per_unit,
    effort_amount_per_unit: li.effort_amount_per_unit,
    sub_items: li.sub_items || [],
    cost: li.cost, margin: li.margin, margin_pct: li.margin_pct,
    cost_ratio: li.cost_ratio,
    updated_at: li.updated_at,
    position: i, _dirty: false,
  }));
  const [rows, setRows] = useState(buildRows(est));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    setRows(buildRows(est));
    setDirty(false);
     
  }, [est.id, est.updated_at]);

  const setCell = (idx, key, val) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [key]: val, _dirty: true } : r));
    setDirty(true);
  };
  const addRow = () => {
    setRows((prev) => [...prev, {
      id: null, name: "", description: "", qty: 1, unit: "unit",
      applied_rate: 0, tax_pct: 0, discount_pct: 0, note: "",
      catalog_item_id: null, effort_mode: "manual", sub_items: [],
      position: prev.length, _dirty: true,
    }]);
    setDirty(true);
    setTimeout(() => {
      const inputs = gridRef.current?.querySelectorAll('[data-testid^="est-cell-name-"]');
      if (inputs?.length) inputs[inputs.length - 1].focus();
    }, 30);
  };
  const removeRow = (idx) => {
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, _delete: true, _dirty: true } : r));
    setDirty(true);
  };
  const applyCatalog = (idx, itemId) => {
    const it = items.find((x) => x.id === itemId);
    if (!it) return;
    setRows((prev) => prev.map((r, i) => i === idx ? {
      ...r,
      name: it.name, description: it.description || r.description,
      unit: it.unit || r.unit,
      applied_rate: it.rate ?? r.applied_rate,
      tax_pct: it.tax_pct ?? r.tax_pct,
      estimated_hours: it.effort_hours ?? r.estimated_hours,
      catalog_item_id: it.id,
      _dirty: true,
    } : r));
    setDirty(true);
  };
  const onNewCatalogItem = (idx, newItem) => {
    setItems((prev) => [newItem, ...prev.filter((x) => x.id !== newItem.id)]);
    setRows((prev) => prev.map((r, i) => i === idx ? {
      ...r,
      name: newItem.name,
      description: newItem.description || r.description,
      unit: newItem.unit || r.unit || "unit",
      applied_rate: newItem.rate ?? r.applied_rate,
      tax_pct: newItem.tax_pct ?? r.tax_pct,
      estimated_hours: newItem.effort_hours ?? r.estimated_hours,
      catalog_item_id: newItem.id,
      _dirty: true,
    } : r));
    setDirty(true);
  };
  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); addRow(); }
  };

  // Iter 241 · Google Sheets tab-delimited paste import
  const handleGridPaste = (e) => {
    const txt = e.clipboardData?.getData("text");
    if (!txt || !txt.includes("\t")) return;
    // Only intercept if focus is on the grid container, not inside an input
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
    e.preventDefault();
    const lines = txt.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return;
    const parsed = lines.map((line) => {
      const cells = line.split("\t").map((c) => c.trim());
      // Best-effort mapping: [name, qty, unit, rate, tax_pct]
      const rec = {
        id: null, name: cells[0] || "",
        description: "", qty: Number(cells[1]) || 1,
        unit: cells[2] || "unit",
        applied_rate: Number(cells[3]) || 0,
        tax_pct: Number(cells[4]) || 0,
        discount_pct: 0, note: "", catalog_item_id: null,
        effort_mode: "manual", sub_items: [],
        _dirty: true,
      };
      return rec;
    }).filter((r) => r.name);
    if (!parsed.length) return;
    setRows((prev) => [...prev, ...parsed]);
    setDirty(true);
    toast.success(`Pasted ${parsed.length} row${parsed.length === 1 ? "" : "s"} from Sheets — click Save changes`);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rows: rows.filter((r) => (r.name || "").trim() || r.id).map((r, i) => ({
          id: r.id || undefined,
          name: r.name || "(untitled)",
          description: r.description || undefined,
          qty: Number(r.qty) || 1, unit: r.unit || "unit",
          applied_rate: Number(r.applied_rate) || 0,
          tax_pct: Number(r.tax_pct) || 0,
          discount_pct: Number(r.discount_pct) || 0,
          note: r.note || undefined,
          catalog_item_id: r.catalog_item_id || undefined,
          estimated_hours: r.estimated_hours != null ? Number(r.estimated_hours) : undefined,
          effort_mode: r.effort_mode || "manual",
          position: i,
          delete: !!r._delete,
        })),
      };
      const { data } = await api.post(`/estimates/${est.id}/line-items/bulk`, payload);
      toast.success(`Saved · ${data.created} added, ${data.updated} updated, ${data.deleted} removed`);
      setDirty(false);
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  // Feb 2026 R293zt · Honour the per-user "view rates" gate when
  // rendering catalog hints. If the viewer can't see rates, strip the
  // ₹rate and the effort-hour tail (hours are a cost signal too — they
  // round-trip via rate × hours into the amount). Keep the category so
  // the picker still disambiguates items.
  const itemOpts = [
    { value: "", label: "— Free type —" },
    ...items.slice().sort((a, b) => (a.name || "").localeCompare(b.name || ""))
             .map((it) => ({
               value: it.id,
               label: it.name,
               hint: ratesHidden
                 ? (it.category || "")
                 : `${it.category || ""} · ${csym}${(it.rate || 0).toLocaleString()}${it.effort_hours ? ` · ${it.effort_hours}h` : ""}`.trim(),
             })),
  ];
  const visibleRows = rows.filter((r) => !r._delete);
  const subtotal = visibleRows.reduce((s, r) => {
    if ((r.effort_mode || "manual") === "auto") {
      return s + Number(r.effort_amount_total || 0);
    }
    return s + (Number(r.qty) || 0) * (Number(r.applied_rate) || 0);
  }, 0);

  return (
    <div className="border border-border overflow-x-auto" data-testid="est-excel-grid" ref={gridRef} onKeyDown={onKeyDown} onPaste={handleGridPaste}>
      <div className="min-w-[900px] sm:min-w-0">
      <div className="px-4 py-2 border-b border-border bg-muted/40 flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide flex items-center gap-2">
          Line items ({visibleRows.length})
          <span className="text-[10px] font-normal normal-case text-muted-foreground">· Paste from Google Sheets supported (tab-delimited)</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canViewEffort && (
            <button type="button" onClick={() => setShowDesignations(true)}
                     className="text-[11px] px-2 py-1 border border-border hover:bg-muted text-muted-foreground"
                     data-testid="est-manage-designations">
              Designations
            </button>
          )}
          <Link to="/app/items" target="_blank" rel="noreferrer"
                 className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1 text-muted-foreground"
                 title="Open catalog in a new tab"
                 data-testid="est-manage-catalog">
            Manage catalog
          </Link>
          <button type="button" onClick={addRow}
                   className="text-[11px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                   data-testid="est-add-row">
            <Plus className="w-3 h-3" /> Add row
            <span className="text-[9px] opacity-70 ml-1">⌘⏎</span>
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving}
                   className="text-[11px] px-2 py-1 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium flex items-center gap-1"
                   data-testid="est-save-lines">
            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-xs min-w-[960px]">
        <thead className="border-b border-border bg-muted/20">
          <tr className="text-left text-[10px] uppercase text-muted-foreground">
            <th className="px-2 py-2 w-6"></th>
            <th className="px-2 py-2 w-8">#</th>
            <th className="px-2 py-2 w-40">Catalog</th>
            <th className="px-2 py-2">Name</th>
            <th className="px-2 py-2 w-16">Qty</th>
            <th className="px-2 py-2 w-16">Unit</th>
            {!ratesHidden && <th className="px-2 py-2 w-24">Rate</th>}
            {!ratesHidden && <th className="px-2 py-2 w-16">Tax %</th>}
            {!ratesHidden && <th className="px-2 py-2 w-24 text-right">Amount</th>}
            <th className="px-2 py-2 w-10"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, i) => r._delete ? null : (
            <ExcelEstimateRow key={r.id || `new-${i}`}
              row={r} idx={i} csym={csym} currency={currency}
              itemOpts={itemOpts} items={items}
              wsId={est.workspace_id}
              estimateId={est.id}
              ratesHidden={ratesHidden} canViewEffort={canViewEffort}
              designations={designations}
              members={members}
              expanded={!!expanded[i]}
              onToggleExpand={() => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }))}
              onSetCell={(k, v) => setCell(i, k, v)}
              onApplyCatalog={(id) => applyCatalog(i, id)}
              onNewCatalogItem={(newItem) => onNewCatalogItem(i, newItem)}
              onRemove={() => removeRow(i)}
              onManageDesignations={() => setShowDesignations(true)}
              onEffortSaved={onSaved} />
          ))}
          {visibleRows.length === 0 && (
            <tr>
              <td colSpan={10} className="px-4 py-6 text-center text-xs text-muted-foreground">
                No line items. Click <b>Add row</b> or press <b>⌘⏎</b> to start.
                <div className="mt-1 text-[10px]">Or paste tab-delimited rows from Google Sheets.</div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>
      {!ratesHidden && (
        <EstimateScenarioRollup
          items={items}
          designations={designations}
          members={members}
          currency={currency}
          csym={csym}
          wsId={est?.workspace_id}
          estimateId={est?.id}
          onSaved={onSaved}
        />
      )}
      {!ratesHidden && (
        <div className="border-t-2 border-border bg-muted/20 p-3 text-xs">
          <div className="flex justify-between max-w-md ml-auto">
            <span className="text-muted-foreground">Subtotal (unsaved preview)</span>
            <span className="font-mono" data-testid="est-subtotal-preview">{csym}{subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          {est.grand_total ? (
            <div className="flex justify-between max-w-md ml-auto pt-1 border-t border-border font-bold text-sm">
              <span>Saved grand total</span>
              <span className="font-mono" data-testid="estimate-grand-total">
                {csym}{Number(est.grand_total).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          ) : null}
          {est.total_margin_pct != null && (
            <div className="flex justify-between max-w-md ml-auto mt-1 pt-1 border-t border-border text-[11px]" data-testid="est-total-margin">
              <span className="text-muted-foreground">Total margin</span>
              <span className="font-mono">
                {csym}{Number(est.total_margin || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <MarginChip pct={est.total_margin_pct} testid="est-total-margin-chip" />
              </span>
            </div>
          )}
        </div>
      )}
      {showDesignations && (
        <DesignationsDialog wsId={est.workspace_id} currency={currency}
          onClose={() => setShowDesignations(false)}
          onChanged={loadDesignations} />
      )}
      </div>
    </div>
  );
}

function ExcelEstimateRow({ row: r, idx: i, csym, currency, itemOpts, items,
                             wsId, estimateId, ratesHidden, canViewEffort, designations, members,
                             expanded, onToggleExpand, onSetCell,
                             onApplyCatalog, onNewCatalogItem, onRemove,
                             onManageDesignations, onEffortSaved }) {
  const isAuto = (r.effort_mode || "manual") === "auto";
  const lineQty = Number(r.qty) || 1;
  // R138ap · Fix: multiply per-unit effort by line qty for total.
  const displayAmount = isAuto
    ? Number(r.effort_amount_total || 0)
    : (Number(r.qty) || 0) * (Number(r.applied_rate) || 0);
  const hoursPerUnit = Number(r.effort_hours_per_unit || 0);
  const displayQty = isAuto ? lineQty : r.qty;
  const displayRate = isAuto && hoursPerUnit > 0
    ? (Number(r.effort_amount_per_unit || 0)).toFixed(2)
    : r.applied_rate;
  const subCount = (r.sub_items || []).length;
  return (
    <>
      <tr data-testid={`est-row-${i}`} className={r._dirty ? "bg-amber-50/40" : ""}>
        <td className="px-1 py-1 text-center align-top">
          {canViewEffort && r.id && (
            <button type="button" onClick={onToggleExpand}
                     className={`p-0.5 hover:text-foreground ${expanded ? "text-primary" : "text-muted-foreground"}`}
                     title={expanded ? "Collapse effort" : "Expand effort"}
                     data-testid={`est-row-expand-${i}`}>
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {subCount > 0 && !expanded && (
                <span className="ml-0.5 text-[9px]">{subCount}</span>
              )}
            </button>
          )}
        </td>
        <td className="px-2 py-1 text-muted-foreground align-top">{i + 1}</td>
        <td className="px-1 py-1">
          <div className="flex items-center gap-1">
            <div className="flex-1 min-w-0">
              <SearchableSelect
                value={r.catalog_item_id || ""}
                onValueChange={(v) => v ? onApplyCatalog(v) : onSetCell("catalog_item_id", null)}
                options={itemOpts}
                placeholder="Free type"
                testid={`est-cell-catalog-${i}`}
              />
            </div>
            <InlineNewItemPopover
              wsId={wsId}
              categories={Array.from(new Set(items.map((it) => it.category).filter(Boolean)))}
              onCreated={(newItem) => onNewCatalogItem(newItem)}
              testid={`est-cell-inline-new-${i}`}
            />
          </div>
        </td>
        <td className="px-1 py-1">
          <div className="flex items-center gap-1">
            <input value={r.name} onChange={(e) => onSetCell("name", e.target.value)}
                    className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                    placeholder="Line item name" data-testid={`est-cell-name-${i}`} />
            {isAuto && (
              <span className="shrink-0 text-[9px] px-1 py-0.5 border border-amber-300 bg-amber-50 text-amber-700 uppercase tracking-wide" title="Amount driven by effort rollup">Auto</span>
            )}
          </div>
          {r.rfq_note && (
            <div className="text-[10px] text-muted-foreground italic mt-0.5 border-l-2 border-amber-300 pl-1.5"
                  title="Note from source RFQ" data-testid={`est-rfq-note-${i}`}>
              RFQ note: {r.rfq_note}
            </div>
          )}
        </td>
        <td className="px-1 py-1">
          <input type="number" step="0.5" value={displayQty}
                  disabled={isAuto}
                  onChange={(e) => onSetCell("qty", e.target.value)}
                  className={`w-full text-xs px-2 py-1 bg-background border border-border tabular-nums focus:border-primary focus:outline-none ${isAuto ? "opacity-60 cursor-not-allowed bg-muted/40" : ""}`}
                  data-testid={`est-cell-qty-${i}`} />
        </td>
        <td className="px-1 py-1">
          <input value={r.unit} onChange={(e) => onSetCell("unit", e.target.value)}
                  className="w-full text-xs px-2 py-1 bg-background border border-border focus:border-primary focus:outline-none"
                  data-testid={`est-cell-unit-${i}`} />
        </td>
        {!ratesHidden && (
          <td className="px-1 py-1">
            <input type="number" step="1" value={displayRate}
                    disabled={isAuto}
                    onChange={(e) => onSetCell("applied_rate", e.target.value)}
                    className={`w-full text-xs px-2 py-1 bg-background border border-border tabular-nums focus:border-primary focus:outline-none ${isAuto ? "opacity-60 cursor-not-allowed bg-muted/40" : ""}`}
                    data-testid={`est-cell-rate-${i}`} />
          </td>
        )}
        {!ratesHidden && (
          <td className="px-1 py-1">
            <input type="number" step="0.5" value={r.tax_pct}
                    onChange={(e) => onSetCell("tax_pct", e.target.value)}
                    className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums focus:border-primary focus:outline-none"
                    data-testid={`est-cell-tax-${i}`} />
          </td>
        )}
        {!ratesHidden && (
          <td className="px-1 py-1 text-right font-mono tabular-nums align-top pt-2" data-testid={`est-cell-amount-${i}`}>
            <div>{csym}{displayAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            {r.margin_pct != null && (
              <MarginChip pct={r.margin_pct} testid={`est-cell-margin-${i}`} />
            )}
          </td>
        )}
        <td className="px-2 py-1 text-right align-top">
          <button type="button" onClick={onRemove}
                   className="text-muted-foreground hover:text-rose-500"
                   aria-label="Delete"
                   data-testid={`est-row-remove-${i}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </td>
      </tr>
      {expanded && canViewEffort && r.id && (
        <tr data-testid={`est-row-effort-${i}`}>
          <td colSpan={ratesHidden ? 7 : 10} className="p-0">
            <EffortBuilder
              line={r}
              designations={designations}
              members={members}
              wsId={wsId}
              estimateId={estimateId}
              currency={currency}
              ratesHidden={ratesHidden}
              effortMode={r.effort_mode || "manual"}
              onEffortModeChange={(m) => { onSetCell("effort_mode", m); }}
              onManageDesignations={onManageDesignations}
              onSaved={onEffortSaved}
            />
          </td>
        </tr>
      )}
    </>
  );
}


function EstEmailPdfDialog({ est, clientContacts, onClose, onSent }) {
  const [to, setTo] = useState((clientContacts.map(c => c.email).filter(Boolean)[0]) || "");
  const [subject, setSubject] = useState(`Estimate ${est.number} · ${est.title}`);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!to.trim()) { toast.error("Recipient required"); return; }
    setBusy(true);
    try {
      const emails = to.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post(`/estimates/${est.id}/email-pdf`,
        { to: emails, subject, message: message || undefined });
      toast.success(`Sent to ${data.sent} recipient(s)`);
      onSent(); onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background border border-border shadow-2xl" data-testid="est-email-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Email PDF</div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">To (comma-separated)</label>
            <input value={to} onChange={(e) => setTo(e.target.value)}
                    className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                    data-testid="est-email-to" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)}
                    className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                    data-testid="est-email-subject" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Message</label>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)}
                       className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                       data-testid="est-email-message" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={send} disabled={busy}
                   className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 font-medium"
                   data-testid="est-email-send">
            {busy ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ────────────────────── Iter 242 · Save as Template ──────────────────────
function SaveTemplateDialog({ est, onClose, onSaved }) {
  const [name, setName] = useState(`${est.title || "Template"} · copy`);
  const [description, setDescription] = useState("");
  const [includeClientDefaults, setIncludeClientDefaults] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post(`/estimates/${est.id}/save-as-template`, {
        name: name.trim(), description: description || undefined,
        include_client_defaults: includeClientDefaults,
      });
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl" data-testid="est-save-template-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Save as Estimate Template</div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Template name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                    data-testid="est-tpl-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Description (optional)</label>
            <textarea rows={3} value={description}
                       onChange={(e) => setDescription(e.target.value)}
                       className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                       data-testid="est-tpl-desc" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={includeClientDefaults}
                    onChange={(e) => setIncludeClientDefaults(e.target.checked)}
                    data-testid="est-tpl-include-client" />
            Also capture client &amp; brand defaults
          </label>
          <div className="text-[10px] text-muted-foreground border border-dashed border-border p-2">
            Snapshot includes: line items (with effort mode / cost ratio),
            sub-item breakdown, global discount, currency, notes.
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={busy}
                   className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50 font-medium"
                   data-testid="est-tpl-save">
            {busy ? "Saving…" : "Save template"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────── Iter 242 · Effort Digest Config ──────────────────────
function EffortDigestDialog({ est, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(!!est.digest_enabled);
  const [recipients, setRecipients] = useState((est.digest_recipient_emails || []).join(", "));
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/estimates/${est.id}/effort-digest/preview`)
      .then((r) => setPreview(r.data)).catch(() => {});
  }, [est.id]);

  const save = async () => {
    setBusy(true);
    try {
      const emails = recipients.split(",").map((s) => s.trim()).filter(Boolean);
      await api.patch(`/estimates/${est.id}`, {
        digest_enabled: enabled,
        digest_recipient_emails: emails,
      });
      toast.success("Digest config saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/effort-digest/send-now`);
      toast.success(`Sent to ${data.sent} recipient${data.sent === 1 ? "" : "s"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-background border border-border shadow-2xl" data-testid="est-digest-dialog">
        <div className="px-4 py-3 border-b border-border font-semibold text-sm">Weekly Effort Digest</div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    data-testid="est-digest-enabled" />
            Enable weekly digest (Friday 09:00 UTC)
          </label>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Recipient emails (comma-separated)</label>
            <textarea rows={2} value={recipients}
                       onChange={(e) => setRecipients(e.target.value)}
                       placeholder="lead@example.com, ops@example.com"
                       className="w-full text-xs px-2 py-1 mt-0.5 bg-background border border-border"
                       data-testid="est-digest-recipients" />
          </div>
          {preview && preview.variance && (
            <div className="border border-border p-3 space-y-1" data-testid="est-digest-preview">
              <div className="text-[10px] uppercase text-muted-foreground">Current variance snapshot</div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="text-muted-foreground">Quoted hours</div>
                <div className="text-right font-mono">{Number(preview.variance.quoted_hours || 0).toFixed(2)} h</div>
                <div className="text-muted-foreground">Logged (last 7 days)</div>
                <div className="text-right font-mono">{Number(preview.variance.logged_hours_7d || 0).toFixed(2)} h</div>
                <div className="text-muted-foreground">Logged (cumulative)</div>
                <div className="text-right font-mono">{Number(preview.variance.logged_hours_total || 0).toFixed(2)} h</div>
                <div className="text-muted-foreground">Variance</div>
                <div className={`text-right font-mono font-semibold ${preview.variance.over_budget ? "text-rose-600" : "text-emerald-700"}`}>
                  {preview.variance.variance_pct != null ? `${preview.variance.variance_pct.toFixed(1)}%` : "—"}
                </div>
              </div>
              {preview.variance.over_budget && (
                <div className="text-[10px] text-rose-700 border border-rose-300 bg-rose-50 px-2 py-1 mt-1">
                  Over budget · logged effort exceeds quoted by 20%+
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-between items-center gap-2">
          <button type="button" onClick={sendNow} disabled={busy || !recipients.trim()}
                   className="text-xs px-3 py-1.5 border border-border hover:bg-muted disabled:opacity-50"
                   data-testid="est-digest-send-now">
            Send test now
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
            <button type="button" onClick={save} disabled={busy}
                     className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50 font-medium"
                     data-testid="est-digest-save">
              {busy ? "Saving…" : "Save config"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

